import { useContext } from 'react';

import { GridColumns, GridRows } from '@visx/grid';
import { DataContext } from '@visx/xychart';

import { useTickValues } from './axis/useTickValues';
import { DEFAULT_X_TICKS, DEFAULT_Y_TICKS } from './axis/utils';

type GridProps = {
  numYTicks?: number;
  numXTicks?: number;
};

/**
 * Renders grid lines for both X and Y axes in a chart.
 *
 * Grid lines are styled according to the current chart theme and aligned with axis tick values.
 *
 * @example
 * ```tsx
 * <XYChart width={500} height={300}>
 *   <Grid numXTicks={5} numYTicks={10} />
 *   <AreaSeries data={data} dataKey="series1" xAccessor={d => d.x} yAccessor={d => d.y} />
 * </XYChart>
 * ```
 */
export const Grid = ({ numYTicks = DEFAULT_Y_TICKS, numXTicks = DEFAULT_X_TICKS }: GridProps) => {
  const { yScale, xScale, innerWidth, theme, innerHeight, margin } = useContext(DataContext) ?? {};
  const { xTickValues, yTickValues } = useTickValues({ numXTicks, numYTicks });

  if (!yScale || !xScale || innerWidth === undefined || innerHeight === undefined) return null;

  const gridStroke = theme?.gridStyles?.stroke;

  return (
    <>
      <GridColumns
        data-testid="grid-columns"
        scale={xScale}
        tickValues={xTickValues}
        height={innerHeight}
        stroke={gridStroke}
        top={margin?.top}
      />
      <GridRows
        data-testid="grid-rows"
        scale={yScale}
        tickValues={yTickValues}
        width={innerWidth}
        stroke={gridStroke}
        left={margin?.left}
      />
    </>
  );
};
